import { DiscoveryApi, FetchApi } from '@backstage/core-plugin-api';
import { WireguardApi, WireguardPeer, WireguardInfo, VpnTestResult } from './WireguardApi';

export class WireguardClient implements WireguardApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly fetchApi: FetchApi;

  constructor(opts: { discoveryApi: DiscoveryApi; fetchApi: FetchApi }) {
    this.discoveryApi = opts.discoveryApi;
    this.fetchApi = opts.fetchApi;
  }

  private async baseUrl(): Promise<string> {
    return this.discoveryApi.getBaseUrl('wireguard');
  }

  async listPeers(): Promise<WireguardPeer[]> {
    const url = `${await this.baseUrl()}/peers`;
    const res = await this.fetchApi.fetch(url);
    if (!res.ok) throw new Error(`Failed to list peers: ${res.status}`);
    const data = await res.json();
    return Array.isArray(data) ? data : Object.values(data);
  }

  async listMyPeers(): Promise<WireguardPeer[]> {
    const url = `${await this.baseUrl()}/mine`;
    const res = await this.fetchApi.fetch(url);
    if (!res.ok) throw new Error(`Failed to list your connections: ${res.status}`);
    const data = await res.json();
    return Array.isArray(data) ? data : Object.values(data);
  }

  async deleteMyPeer(id: string): Promise<void> {
    const url = `${await this.baseUrl()}/mine/${id}`;
    const res = await this.fetchApi.fetch(url, { method: 'DELETE' });
    if (!res.ok) throw new Error(`Failed to delete connection: ${res.status}`);
  }

  async regenerateMyPeer(id: string): Promise<WireguardPeer> {
    const url = `${await this.baseUrl()}/mine/${id}/regenerate`;
    const res = await this.fetchApi.fetch(url, { method: 'POST' });
    if (!res.ok) throw new Error(`Failed to regenerate connection: ${res.status}`);
    return res.json();
  }

  async createPeer(name: string): Promise<WireguardPeer> {
    const url = `${await this.baseUrl()}/peers`;
    const res = await this.fetchApi.fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name }),
    });
    if (!res.ok) throw new Error(`Failed to create peer: ${res.status}`);
    return res.json();
  }

  async deletePeer(id: string): Promise<void> {
    const url = `${await this.baseUrl()}/peers/${id}`;
    const res = await this.fetchApi.fetch(url, { method: 'DELETE' });
    if (!res.ok) throw new Error(`Failed to delete peer: ${res.status}`);
  }

  async enablePeer(id: string): Promise<void> {
    const url = `${await this.baseUrl()}/peers/${id}/enable`;
    const res = await this.fetchApi.fetch(url, { method: 'POST' });
    if (!res.ok) throw new Error(`Failed to enable peer: ${res.status}`);
  }

  async disablePeer(id: string): Promise<void> {
    const url = `${await this.baseUrl()}/peers/${id}/disable`;
    const res = await this.fetchApi.fetch(url, { method: 'POST' });
    if (!res.ok) throw new Error(`Failed to disable peer: ${res.status}`);
  }

  async getPeerConfig(id: string): Promise<string> {
    const url = `${await this.baseUrl()}/peers/${id}/config`;
    const res = await this.fetchApi.fetch(url);
    if (!res.ok) throw new Error(`Failed to get config: ${res.status}`);
    return res.text();
  }

  getPeerQrUrl(id: string): string {
    // This returns a relative path; the component will resolve it via discoveryApi
    return `/api/wireguard/peers/${id}/qr`;
  }

  async getInfo(): Promise<WireguardInfo> {
    const url = `${await this.baseUrl()}/info`;
    const res = await this.fetchApi.fetch(url);
    if (!res.ok) throw new Error(`Failed to get info: ${res.status}`);
    return res.json();
  }

  async testConnection(): Promise<VpnTestResult> {
    const url = `${await this.baseUrl()}/test`;
    const res = await this.fetchApi.fetch(url);
    return res.json();
  }

  getScriptUrl(name: string): string {
    return `/api/wireguard/scripts/${name}`;
  }
}
